import React from 'react';
import { css } from '@emotion/css';
import castArray from 'lodash/castArray';

import { setIn } from '@lyearn/core/utils/immutable';
import { renderErrorMessage } from '@lyearn/form/src/Form/components/InputFields/renderers';
import { ErrorPosition } from '@lyearn/form/src/Form/types';
import { useTranslation } from '@lyearn/i18n';
import { cn } from '@lyearn/style';
import { Box, Select, Stack, Typography } from '@lyearn/ui';
import { getSelectOptionLabel } from '@/components/Form/components/Select/getOptionLabel';

import { DaysOfWeek } from '../../../../conts';
import { FrequencyComponentProps } from '../../../../types';
import { getOptions } from '../../../InputComponents/Weekdays';

const maxWidth = css`
  max-width: 100%;
`;

const inputStyle = css`
  min-width: 0 !important;
  width: 0 !important;
  margin-inline-start: 0;
  margin-inline-end: 0;
`;

const CLASSES = {
  input: inputStyle,
};

const VALUE_COUNT = 3;

const MultiWeekday = ({
  value,
  onChange,
  multiple = true,
  disabled,
  error,
  size,
}: FrequencyComponentProps & { multiple?: boolean; error?: JSX.Element }) => {
  const { t } = useTranslation();
  const days = value.custom?.daysOfWeek ? value.custom.daysOfWeek : [DaysOfWeek[0].id];

  const options = React.useMemo(() => getOptions(DaysOfWeek, t), [t]);

  const onValueChange = React.useCallback(
    (newValue: string | string[]) => {
      onChange(setIn(value, ['custom', 'daysOfWeek'], castArray(newValue)));
    },
    [onChange, value],
  );

  const renderTags = React.useCallback(
    (values: typeof options) => {
      const typographyVariant = size === 'medium' ? 'body-short-02' : 'body-short-01';

      if (values?.length === 1) {
        return (
          <Typography color="text-text-paragraph" numberOfLines={1} variant={typographyVariant}>
            {values[0].label}
          </Typography>
        );
      }

      const labels = values?.reduce((acc, it, index) => {
        if (index > VALUE_COUNT - 1) return acc;
        if (it) acc.push(it.shortLabel);

        return acc;
      }, [] as string[]);

      return (
        <Box className={cn(maxWidth, 'flex items-center overflow-hidden')}>
          <Typography
            className="inline-block"
            color="text-text-paragraph"
            numberOfLines={1}
            variant={typographyVariant}>
            {labels?.join(', ')}
          </Typography>
          {values?.length <= VALUE_COUNT ? null : (
            <Typography
              className="inline-block ps-4 pe-8"
              color="text-text-placeholder"
              variant={typographyVariant}>
              {`+${values?.length - VALUE_COUNT}`}
            </Typography>
          )}
        </Box>
      );
    },
    [size],
  );

  return (
    <Stack className="flex flex-1" direction="col" gapY={8}>
      <Select
        disableClearable
        blurOnSelect={!multiple}
        className="w-full"
        classes={days.length ? CLASSES : undefined}
        disableCloseOnSelect={true}
        disabled={disabled}
        error={Boolean(error)}
        getOptionLabel={getSelectOptionLabel}
        multiple={multiple}
        options={options}
        placeholder={t('Common:Weekdays.Placeholder')}
        renderTags={renderTags}
        renderValue={(value) => renderTags(value ? [value] : value)}
        size={size ?? 'large'}
        value={multiple ? days : days?.[0]}
        onChange={onValueChange}
      />
      {error
        ? renderErrorMessage({ errorMessage: error, errorPosition: ErrorPosition.Custom })
        : null}
    </Stack>
  );
};

export default MultiWeekday;
